import {ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, SimpleChanges} from "@angular/core";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {DateTime} from '@webmate-web-core/shared/types/base-types';


@Component({
    selector: 'wm-mailcontent-artifact-mail-details',
    templateUrl: './mail-details.component.html',
    styleUrls: ['./mail-details.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MailDetailsComponent implements OnChanges, OnDestroy {
    @Input() from: string;
    @Input() to: string;
    @Input() subject: string;
    @Input() body: string;
    @Input() hasHtmlBody: boolean;
    @Input() sentDate: DateTime;

    htmlBody: SafeHtml;

    pixelSrcs: string[] = [];
    private iframeResizeObserver?: ResizeObserver;
    private iframeImageCleanup: (() => void)[] = [];
    private iframeResizeTimeouts: number[] = [];

    constructor(private domSanitizer: DomSanitizer) {
    }

    ngOnChanges(changes: SimpleChanges) {
        this.cleanupIframeResize();
        this.pixelSrcs = [];
        if (!!this.body && !!this.hasHtmlBody) {
            this.htmlBody = this.domSanitizer.bypassSecurityTrustHtml(this.body);
            // parse src attribute from string of img HTML elements
        }
        const regex = /<img[^>]+src="([^">]+)"/g;
        const combinedString = ((this.htmlBody || '') + (this.body || '')).toString();
        let m;
        while (m = regex.exec(combinedString)) {
            this.pixelSrcs.push(m[1]);
        }
    }

    ngOnDestroy() {
        this.cleanupIframeResize();
    }

    onIframeLoad(iframe: HTMLIFrameElement) {
        this.cleanupIframeResize();

        const iframeDocument = iframe.contentDocument;
        if (!iframeDocument) {
            return;
        }

        this.normalizeIframeContent(iframeDocument);
        this.resizeIframeToContent(iframe);

        if (iframeDocument.documentElement) {
            iframeDocument.documentElement.style.maxWidth = '100%';
            iframeDocument.documentElement.style.boxSizing = 'border-box';
            iframeDocument.documentElement.style.overflowX = 'hidden';
            iframeDocument.documentElement.style.overflowY = 'hidden';
        }
        if (iframeDocument.body) {
            iframeDocument.body.style.maxWidth = '100%';
            iframeDocument.body.style.boxSizing = 'border-box';
            iframeDocument.body.style.overflowX = 'hidden';
            iframeDocument.body.style.overflowY = 'hidden';
        }

        const resize = () => this.resizeIframeToContent(iframe);
        const images = Array.from(iframeDocument.images || []);

        for (const image of images) {
            image.addEventListener('load', resize);
            image.addEventListener('error', resize);
            this.iframeImageCleanup.push(() => {
                image.removeEventListener('load', resize);
                image.removeEventListener('error', resize);
            });
        }

        if (typeof ResizeObserver !== 'undefined') {
            this.iframeResizeObserver = new ResizeObserver(() => resize());
            if (iframeDocument.body) {
                this.iframeResizeObserver.observe(iframeDocument.body);
            }
            if (iframeDocument.documentElement) {
                this.iframeResizeObserver.observe(iframeDocument.documentElement);
            }
        }

        for (const delay of [0, 50, 150, 500]) {
            const timeoutId = window.setTimeout(() => resize(), delay);
            this.iframeResizeTimeouts.push(timeoutId);
        }
    }

    private resizeIframeToContent(iframe: HTMLIFrameElement) {
        requestAnimationFrame(() => {
            const iframeDocument = iframe.contentDocument;
            if (!iframeDocument) {
                return;
            }

            const body = iframeDocument.body;
            const documentElement = iframeDocument.documentElement;
            const contentHeight = Math.max(
                body?.scrollHeight || 0,
                body?.offsetHeight || 0,
                body?.getBoundingClientRect().height || 0,
                documentElement?.scrollHeight || 0,
                documentElement?.offsetHeight || 0,
                documentElement?.getBoundingClientRect().height || 0,
                320
            );

            iframe.style.height = `${contentHeight}px`;
            iframe.style.width = '100%';
        });
    }

    private normalizeIframeContent(iframeDocument: Document) {
        const styleId = 'wm-mail-details-iframe-normalize';
        if (iframeDocument.getElementById(styleId)) {
            return;
        }

        const styleElement = iframeDocument.createElement('style');
        styleElement.id = styleId;
        styleElement.textContent = `
            html, body {
                margin: 0 !important;
                padding: 0 10px 0 4px !important;
                width: 100% !important;
                max-width: 100% !important;
                background: transparent !important;
                overflow-x: hidden !important;
                box-sizing: border-box !important;
            }

            *, *::before, *::after {
                box-sizing: border-box !important;
            }

            img, table, pre, iframe {
                max-width: 100% !important;
            }
        `;

        iframeDocument.head?.appendChild(styleElement);
    }

    private cleanupIframeResize() {
        this.iframeResizeObserver?.disconnect();
        this.iframeResizeObserver = undefined;

        for (const cleanup of this.iframeImageCleanup) {
            cleanup();
        }
        this.iframeImageCleanup = [];

        for (const timeoutId of this.iframeResizeTimeouts) {
            window.clearTimeout(timeoutId);
        }
        this.iframeResizeTimeouts = [];
    }
}
